import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import "react-tooltip/dist/react-tooltip.css";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  deleteCollectionItem,
  getCollectionsList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateCollectionsBunch,
} from "store/gameCenter/collections/slice";
import { gameCenterLimits } from "configs/limits";
import { ModalTypes, GameCenterFormTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { usePermissionHelper } from "helpers/usePermissionHelper";

import ToggleBtn from "components/buttons/ToggleBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import ActionBtn from "components/buttons/ActionBtn";
import Pagination from "components/Pagination";
import GameCenterFilters from "../GameCenterFilters";

import editIcon from "assets/icons/edit-icon.svg";
import eyeIcon from "assets/icons/eye.svg";
import eyeOffIcon from "assets/icons/eye-off.svg";
import listIcon from "assets/icons/list-icon.svg";
import gridIcon from "assets/icons/grid-icon.svg";
import {
  NotificationTypes,
  showNotification,
} from "../../../store/notifications/slice";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import { useGuideModal } from "modals/guides/useGuideModal";
import { GuideButton } from "modals/guides/GuideButton";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import ImageWithFallback from "helpers/ImageWithFallback";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const CollectionsList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { openGuide } = useGuideModal();
  const activeCollectionId = searchParams.get("entityId");

  const permissions = usePermissionHelper();

  const [order, setOrder] = useState<{
    title: string;
    slug: string;
    countOfGames: string;
  }>({
    title: "none",
    slug: "none",
    countOfGames: "none",
  });
  const [activeView, setView] = useState<string>("table");
  const [currentLimit, setLimit] = useState<number>(gameCenterLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [active, setActive] = useState<
    { value: number | string; label: string }[]
  >([]);
  const [categories, setCategories] = useState<
    { value: number | string; label: string }[]
  >([]);

  const options = [
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("hide"),
      value: "hide",
    },
  ];

  const collectionsList = useAppSelector((store) => store.collections.list);
  const isListFetched = useAppSelector(
    (store) => store.collections.isListFetched,
  );
  const totalItems = useAppSelector((store) => store.collections.totalItems);
  const totalPages = useAppSelector((store) => store.collections.totalPages);
  const activePage = useAppSelector((store) => store.collections.activePage);
  const selectedItems = useAppSelector(
    (store) => store.collections.selectedItems,
  );
  const processingItemId = useAppSelector(
    (store) => store.collections.processingItemId,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'collections',
    isListFetched
  });

  useEffect(() => {
    if (permissions && !permissions.read) {
      navigate("/dashboard");
    }
    const grid = localStorage.getItem("grid");
    if (grid) {
      setView(grid);
    }
  }, [navigate, permissions]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getCollectionsList({
          limit: currentLimit,
          offset: activePage - 1,
          isActive:
            active[0] && active[0].value !== 3
              ? (active[0].value as number)
              : undefined,
          title: search,
          categoryIds: categories.map((el) => el.value as number),
          order,
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };
  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };
  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleOpenCreateModal = () => {
    if (permissions?.create) {
      navigate(
        `${location.pathname}?activeModal=${ModalTypes.gameCenter}&gModalType=${GameCenterFormTypes.collection}`,
      );
      dispatch(openModal({ activeModal: ModalTypes.gameCenter }));
    }
  };
  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.gameCenter}&gModalType=${GameCenterFormTypes.collection}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.gameCenter }));
  };
  const handleDeleteItem = async (id: number) => {
    if (!permissions?.delete) return;

    const resultCollection = await dispatch(deleteCollectionItem(id ?? 0));
    if (resultCollection?.payload?.status === "OK") {
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityDeleteSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } else {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityDeleteError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const handleActionClick = async (e: string) => {
    if (!permissions?.update) return;

    try {
      await dispatch(
        updateCollectionsBunch({
          ids: selectedItems,
          isActive: e === "activate" ? 1 : 0,
        }),
      );
    } catch (error) {
      console.log(error, "error");
    }
  };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <GameCenterFilters
        searchValue={search}
        handleSearch={handleFilterSearch}
        selectedActive={active}
        handleUpdateActive={(e) => setActive(e)}
        selectedCategories={categories}
        handleUpdateCategories={(e) => setCategories(e)}
        activeBeforeCategories
        handleApply={handleApply}
      />
      <div className="page-list--with-filter-content">
        <>
          <div className="page-list-header">
            {permissions?.create && (
              <div className="page-list-header--box">
                <ActionBtn
                  txt={t("addCollection")}
                  handleClick={handleOpenCreateModal}
                />
              </div>
            )}
            <div className="page-list-header--box page-list-header--box--right">
              <RefreshButton
                position="right"
                disabled={isRefreshRequested}
                isLoading={isRefreshRequested}
                onClick={() => refresh()}
              />
            </div>

            <div className="page-list-header--box page-list-header--box-actions">
              <ToggleBtn
                handleClick={(v) => {
                  localStorage.setItem("grid", v);
                  setView(v);
                }}
                activeValue={activeView}
                disabled={collectionsList.length === 0}
                icons={[
                  {
                    url: listIcon,
                    value: "table",
                  },
                  {
                    url: gridIcon,
                    value: "grid",
                  },
                ]}
              />
              <GuideButton onClick={() => openGuide("gamecentre")} />
              {permissions?.update && (
                <DefaultDropdown
                  onSelect={(e) => {
                    handleActionClick(e as string);
                  }}
                  placeholder={t("chooseAction")}
                  options={options}
                  isSmall
                  disabled={selectedItems.length === 0}
                />
              )}
            </div>
          </div>

          {!isListFetched && (
            <>
              <DataTableSkeleton columnsCount={8} rowsCount={12} />
              <PaginationSkeleton />
            </>
          )}

          {isListFetched && (
            <>
              <div
                className={`game-table-wrap ${activeView === "table" ? "" : "grid"}`}
              >
                {collectionsList.length === 0 && (
                  <EmptyState message={t("noCollectionsFound")} />
                )}

                {collectionsList.length > 0 && (
                  <>
                    {activeView === "table" ? (
                      <Table
                        data={collectionsList}
                        idField="id"
                        order={order}
                        selectedItems={selectedItems}
                        onAllSelect={handleAllClick}
                        onSelect={(id) => handleItemClick(id)}
                        onDelete={(id) => handleDeleteItem(id)}
                        onEdit={(id) => handleOpenEditModal(id)}
                        onView={(id) => handleOpenEditModal(id)}
                        hasEdit={permissions?.update}
                        hasView={!permissions?.update}
                        hasDelete={permissions?.delete}
                        hasCheckbox={permissions?.update}
                        activeRowId={activeCollectionId}
                        onSort={(id) => (id ? handleOrderClick(id) : {})}
                        processingId={processingItemId}
                        columns={{
                          hiddenColumns: ['icon']
                        }}
                      />
                    ) : (
                      <div className="game-grid">
                        {collectionsList.map((el) => (
                          <div className="game-item big" key={el.id}>
                            <div className={`game-item-img ${
                                el?.isActive ? "active" : "no-active"
                              }`}
                            >
                              <ImageWithFallback
                                src={el.image}
                                alt={el.title}
                                className="game-item-img__image"
                              />
                            </div>
                            <div className="game-item-content">
                              <div className="game-item-content--title">
                                {el.title}
                              </div>
                              <div className="game-item-content--actions">
                                {permissions?.update && (
                                  <div
                                    onClick={() => handleItemClick(el.id)}
                                    className={`def-check-control ${
                                      selectedItems.includes(el.id)
                                        ? "checked"
                                        : ""
                                    }`}
                                  />
                                )}
                                <div className="provider">
                                  {el.countOfGames}
                                </div>
                                <div
                                  className="option"
                                  onClick={() => handleOpenEditModal(el.id)}
                                >
                                  <img
                                    src={
                                      permissions?.update
                                        ? editIcon
                                        : String(activeCollectionId) === String(el.id)
                                          ? eyeOffIcon
                                          : eyeIcon
                                    }
                                    alt={permissions?.update ? "Edit Icon" : "View Icon"}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {collectionsList.length > 0 && (
                <Pagination
                  handleLimitClick={(e) => {
                    setLimit(e);
                    dispatch(handlePaginate(1));
                  }}
                  limitsArray={gameCenterLimits}
                  activePage={activePage}
                  limit={currentLimit}
                  totalItems={totalItems}
                  totalPages={totalPages}
                  handleNextClick={handleNextClick}
                  handlePrevClick={handlePrevClick}
                />
              )}
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default CollectionsList;
